import { Button, ButtonGroup, Status, StatusProps, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';
import { AgFinancingPhaseStatus } from '@indigo-ag/schema';
import i18next from 'i18next';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { StatusColors } from '@indigo-ag/component-module/dist/commonjs/components/Status/_implementation/constants';
import { UserContext } from 'core/components/GateWay/GateWay';
import { BackgroundCheckModalDecision } from 'features/brazil-grower/components/BackgroundCheckModal/BackgroundCheckModalDecision';
import { EscalateToManagerButton } from '../../shared/background-check/components/EscalateToManagerButton/EscalateToManagerButton';
import { syncBackgroundChecksInGroup, financingAgFinancing } from 'features/brazil-grower/queries';
import { getOpportunityContactRolesAndBackgroundCheckGroup } from 'features/brazil-grower/queries/backgroundCheck.queries';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { text } from 'shared';

const getStatusProps = (phaseStatus: AgFinancingPhaseStatus): { label: string } & Partial<StatusProps> => {
  switch (phaseStatus) {
    case AgFinancingPhaseStatus.CLEAR:
      return {
        label: i18next.t('cleared'),
        variant: 'success'
      };
    case AgFinancingPhaseStatus.AWAITING_MANAGER_APPROVAL:
      return {
        label: i18next.t('waitingForApproval'),
        color: 'yellow' as StatusColors
      };
    case AgFinancingPhaseStatus.DENIED:
      return {
        label: i18next.t('denied'),
        color: 'red' as StatusColors
      };
    case AgFinancingPhaseStatus.NOT_STARTED:
      return {
        label: i18next.t('notStarted')
      };
    case AgFinancingPhaseStatus.IN_PROGRESS:
      return {
        label: i18next.t('inProgress'),
        color: 'yellow' as StatusColors
      };
    default:
      return {
        label: ''
      };
  }
};

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

interface Props {
  canEscalate?: boolean;
  canSetDecision?: boolean;
  phaseStatus: AgFinancingPhaseStatus | undefined;
}

export const StatusActionBar = ({ canEscalate, canSetDecision, phaseStatus }: Props) => {
  /**
   * Context
   */
  const { isCreditLead, isCreditAnalyst, isSuper } = useContext(UserContext);
  const { orderInfo, agFinancingId } = useContext(FinanceProcessContext);

  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const opportunityId = orderInfo?.opportunities?.[0].id;

  /**
   * Mutations
   */
  const [syncBackgroundChecksInGroupFn] = useMutation(syncBackgroundChecksInGroup);

  const { addFlashMessage } = useFlashMessage();

  const syncBackgroundChecks = () => {
    setIsSyncing(true);
    syncBackgroundChecksInGroupFn({
      variables: {
        referenceId: opportunityId
      },
      refetchQueries: [
        { query: getOpportunityContactRolesAndBackgroundCheckGroup, variables: { opportunityId } },
        { query: financingAgFinancing, variables: { agFinancingId } }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        addFlashMessage({
          content: i18next.t('backgroundCheckContactsSynced'),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsSyncing(false);
      });
  };

  return (
    <>
      <BackgroundCheckModalDecision
        open={modalOpen}
        closeModal={() => setModalOpen(false)}
        status={phaseStatus}
        notesRequired={
          phaseStatus && [AgFinancingPhaseStatus.CLEAR, AgFinancingPhaseStatus.DENIED].includes(phaseStatus)
        }
      />
      <Container>
        <StatusWrapper>
          {phaseStatus ? <Status {...getStatusProps(phaseStatus)} /> : null}
          <Button onClick={syncBackgroundChecks} size="small" busy={isSyncing}>
            {text.toTitleCase(i18next.t('sync'))}
          </Button>
        </StatusWrapper>
        <ButtonGroup style={{ margin: 0 }}>
          {canEscalate && (isCreditLead || isCreditAnalyst || isSuper) && <EscalateToManagerButton />}
          <Button disabled={!canSetDecision} onClick={() => setModalOpen(true)} size="small">
            {i18next.t('decision')}
          </Button>
        </ButtonGroup>
      </Container>
    </>
  );
};
